import { getClientStream } from '@confluence/ssr-data-streaming';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities';

declare global {
	interface Window {
		__dataFetcherForSuspense__?: Record<string, SuspenseableDataFetcher>;
	}
}

let mockSuspenseDelayAccumulated = 0;

type PromiseState = 'pending' | 'resolved' | 'error';

export type SuspenseableDataFetcher = {
	get: () => Promise<void>;
	getState: () => PromiseState;
	getError: () => Error | null;
	update: (state: PromiseState, result: any) => void;
	read: () => any;
};

export function getDataFetcherForSuspense(dataFetcherName: string): SuspenseableDataFetcher | null {
	if (!window.__SSR_REACT_STREAMING__) {
		return null;
	}

	const dataFetcher = window.__dataFetcherForSuspense__?.[dataFetcherName] ?? null;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		if (!dataFetcher) {
			if (!window.__dataFetcherForSuspense__) {
				window.__dataFetcherForSuspense__ = {};
			}

			let pendingResolve = false;
			const dataFetcher = createDataFetcher(
				dataFetcherName,
				new Promise<void>((resolve) => {
					// Please note done here does NOT mean successful. It could be server-side errored.
					// In that case we will resume hydration so that normal Apollo error handling can take care of it.
					getClientStream('data-fetcher-done').addListener((event) => {
						if (event.name === dataFetcherName) {
							pendingResolve = true;
							resolve();
						}
					});
				}),
			);
			// addListener will get called synchronously when an event already presented before the listener is added.
			// In this case .read() is still pending but we actually have the result already.
			if (pendingResolve) {
				pendingResolve = false;
				// We are not passing result to client for now. Only signaling the done.
				dataFetcher.update('resolved', undefined);
			}
			return (window.__dataFetcherForSuspense__[dataFetcherName] = dataFetcher);
		}
	}
	return dataFetcher;
}

export function createDataFetcherForSuspense(
	dataFetcherName: string,
	promise: Promise<any> | Promise<any>[] | null,
) {
	promise = promise ?? Promise.resolve();

	if (Array.isArray(promise)) {
		promise = Promise.all(promise);
	}

	if (!window.__SSR_REACT_STREAMING__) {
		return promise;
	}

	if (!window.__dataFetcherForSuspense__) {
		window.__dataFetcherForSuspense__ = {};
	}

	const dataFetcher = window.__dataFetcherForSuspense__[dataFetcherName];
	if (!dataFetcher) {
		const urlParams = new URLSearchParams(window.location.search);
		const mockDelay = urlParams.get('MOCK_SUSPENSE_QUERYDELAY');
		// If MOCK_SUSPENSE_QUERYDELAY = 1000, we want first data fetcher to have delay of 1000ms then second one to have 2000ms
		// In this case, we can see the suspense is really working. They should appear piece by piece.
		mockSuspenseDelayAccumulated += mockDelay ? parseInt(mockDelay) : 0;

		window.__dataFetcherForSuspense__[dataFetcherName] = createDataFetcher(
			dataFetcherName,
			promise,
			mockSuspenseDelayAccumulated,
		);
	}

	return Promise.resolve();
}

// export for testing
export function resetDelay() {
	mockSuspenseDelayAccumulated = 0;
}

// export for testing
export function createDataFetcher(
	dataFetcherName: string,
	promise: Promise<any>,
	delay: number = 0,
): SuspenseableDataFetcher {
	let dataResult: any = null;
	let errorResult: any = null;
	let promiseState: PromiseState = 'pending';

	const suspender = new Promise<void>((resolve) => {
		promise
			.then((result) => {
				if (delay > 0) {
					setTimeout(() => {
						dataResult = result;
						promiseState = 'resolved';
						resolve();
					}, delay);
				} else {
					dataResult = result;
					promiseState = 'resolved';
					resolve();
				}
			})
			.catch((error) => {
				error.dataFetcherName = dataFetcherName;
				errorResult = error;
				promiseState = 'error';
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.error(
						`Error in data fetcher ${error.dataFetcherName} for suspense:`,
						error.stack,
					);
				}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					ssrPrepareErrorsClient.submitError(error);
				}
				resolve();
			});
	});

	return {
		get() {
			return suspender;
		},
		getError() {
			return errorResult;
		},
		getState() {
			return promiseState;
		},
		update(state: PromiseState, result: any) {
			promiseState = state;
			if (state === 'resolved') {
				dataResult = result;
			} else if (state === 'error') {
				errorResult = result;
			}
		},
		read() {
			if (promiseState === 'pending') {
				throw suspender;
			} else if (promiseState === 'error') {
				// Data fetcher is a wrapper of preloader. If an error happened we don't want to blow up the tree too early.
				// Return null here to signal the Suspense to continue.
				// The normal error handling in the component will take care of it.
				return null;
			} else if (promiseState === 'resolved') {
				return dataResult;
			}
		},
	};
}
